import { createFileLogger } from './logger';

const log = createFileLogger('LOGOUT_UTILS');

/**
 * Comprehensive list of all auth-related localStorage keys
 * This is the single source of truth for what needs to be cleared
 */
const AUTH_STORAGE_KEYS = {
  // Auth tokens
  tokens: ['authToken', 'id_token', 'refresh_token'],
  
  // Pending OAuth context
  pending: [
    'pendingFunnelId',
    'pendingPersona',
    'pendingPageGroup',
    'pendingPageSlug',
    'pendingRedirectTo',
    'pendingWorkflowContext',
    'pendingWorkflowContext_timestamp',
  ],
  
  // Session/redirect data
  session: [
    'redirectAfterLogin',
    'lastAuthRedirect',
    'userSpaceId',
    'userWelcomeDocId',
    'signupEmail',
  ],
  
  // GalBox status
  galbox: ['galbox-inactive', 'galbox-inactive-name'],
} as const;

/**
 * Clear ALL auth-related data from localStorage and sessionStorage
 * This is the single source of truth for logout cleanup
 */
export function clearAllAuthState(): void {
  log.log('🧹 [LOGOUT] Starting comprehensive auth state cleanup...');
  
  let clearedCount = 0;
  
  // Clear all localStorage keys
  Object.values(AUTH_STORAGE_KEYS).flat().forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared localStorage: ${key}`);
    }
  });
  
  // Clear sessionStorage
  if (sessionStorage.getItem('landing_hero_context')) {
    sessionStorage.removeItem('landing_hero_context');
    clearedCount++;
    log.log('  ✓ Cleared sessionStorage: landing_hero_context');
  }
  
  log.log(`🧹 [LOGOUT] Cleanup complete - cleared ${clearedCount} items`);
}

/**
 * Perform a complete logout:
 * 1. Clear all localStorage/sessionStorage
 * 2. Optionally call Auth0 logout
 * 3. Redirect to home
 */
export function performCompleteLogout(options: {
  auth0Logout?: (params: any) => void;
  hasAuth0User: boolean;
}): void {
  log.log('🚪 [LOGOUT] Starting complete logout process...');
  
  // Step 1: Clear all auth state
  clearAllAuthState();
  
  // Step 2: Auth0 logout if applicable
  if (options.hasAuth0User && options.auth0Logout) {
    log.log('🚪 [LOGOUT] Performing Auth0 logout...');
    options.auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  } else {
    // Step 3: Redirect to home (Auth0 logout handles redirect itself)
    log.log('🚪 [LOGOUT] Redirecting to home...');
    window.location.href = '/';
  }
}

